import {EntityId} from '@reduxjs/toolkit';
import {useCallback} from 'react';
import {useNavigate} from 'react-router';
import {AccountId, MethodId, MethodType} from '~/entities/operations-fiat';
import {OperableAccount, OperableTarget, OperationModel} from '~/redux/finances/domain';

export type NavigateToOperationParams = {
    accountId: AccountId;
    methodType: MethodType | undefined;
    methodId: MethodId | undefined;
};

export function useNavigateToOperation(operation: OperationModel.Type, operations: OperationModel[]) {
    const navigate = useNavigate();

    const createOperationPath = (
        account: {
            id: EntityId;
            type: OperableAccount.Type;
        },
        target: {
            id: EntityId;
            type: OperableTarget.Type;
        } | null
    ) => {
        if (!target) {
            return `/operation/${operation}/${account.type}/${account.id}`;
        }
        return `/operation/${operation}/${account.type}/${account.id}/${target.type}/${target.id}`;
    };

    const navigateToOperation = useCallback(
        (
            account: {
                id: EntityId;
                type: OperableAccount.Type;
            },
            target: {
                id: EntityId;
                type: OperableTarget.Type;
            } | null
        ) => {
            const targetOperation = operations.find(
                op => op.account.id === account.id && op.target.type === target?.type && op.target.id === target.id
            );
            if (targetOperation) {
                navigate(createOperationPath(targetOperation.account, targetOperation.target), {replace: true});
                return;
            }
            const closestOperation = operations.find(op => op.account.id === account.id);
            if (closestOperation) {
                navigate(createOperationPath(closestOperation.account, closestOperation.target), {replace: true});
                return;
            }
            navigate(createOperationPath(account, null), {replace: true});
        },
        [operations]
    );

    return {navigateToOperation, createOperationPath};
}
